/**
 * <CardList> — une seule carte, plusieurs lignes, un filet entre elles (molecule).
 *
 * Trois séances = UNE carte à trois lignes, pas trois cartes. Un écran qui empile des cadres
 * fait lire un cadre à la place de son contenu ; la carte-liste garde une seule ombre et laisse
 * les séparateurs faire le découpage.
 *
 * Elle ne dessine que la boîte et les filets : chaque enfant garde sa propre marge intérieure,
 * parce qu'une ligne compacte et un bloc « en grand » n'ont pas le même retrait. `<CardListRow>`
 * pose celui d'une ligne ordinaire.
 *
 * Réutilise `<Card>` — la surface, le rayon et l'ombre ne se redessinent pas ici : une carte-liste
 * porte l'ombre unique du livre comme n'importe quelle autre carte.
 */
import React from "react"
import { Pressable, View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { type ElevationKey } from "../../theme/tokens/elevation"
import { type SpacingKey } from "../../theme/tokens/spacing"
import { shadowStyle } from "../../utils/shadow"
import { Card } from "../Card"

export type CardListProps = {
    children: React.ReactNode
    /** `"card"` par défaut, comme `<Card>` ; `"none"` pour une carte-liste encastrée. */
    elevation?: ElevationKey
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function CardList(props: CardListProps) {
    const { children, elevation = "card", style, testID } = props

    const items = React.Children.toArray(children).filter(Boolean)

    return (
        <Card p="0" elevation={elevation} style={[styles.card, style]} testID={testID}>
            {items.map((child, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <View key={index} style={index > 0 ? styles.divided : undefined}>
                    {child}
                </View>
            ))}
        </Card>
    )
}

export type CardListRowProps = {
    children: React.ReactNode
    /** Retrait horizontal — `"4"` par défaut (`"5"` pour le bloc en grand). */
    px?: SpacingKey
    /** Retrait vertical — `"3"` par défaut. */
    py?: SpacingKey
    /**
     * Le filet au-dessus de la ligne, quand plusieurs lignes tiennent dans UN enfant de la carte —
     * les lignes squelettes d'un « Voir plus » en cours, posées sous un seul balayage. Ailleurs,
     * c'est `<CardList>` qui pose le filet entre ses enfants.
     */
    divided?: boolean
    style?: StyleProp<ViewStyle>
    testID?: string
}

/** Le retrait d'une ligne de carte-liste, pour que toutes les lignes s'alignent. */
export function CardListRow(props: CardListRowProps) {
    const { children, px = "4", py = "3", divided = false, style, testID } = props

    return (
        <View testID={testID} style={[styles.row(px, py), divided ? styles.divided : undefined, style]}>
            {children}
        </View>
    )
}

export type CardListLinkRowProps = {
    /** Le mot qui porte le geste — « Voir plus », « Ajouter un contrôle ». */
    label: string
    /** Complément en encre secondaire, sur la même ligne — « · Tous mes lives ». */
    hint?: string
    /** Glyphe de tête, quand le geste ajoute quelque chose. */
    iconName?: IconName
    onPress?: () => void
    testID?: string
}

/**
 * Le pied d'une carte-liste : un lien, pas un bouton.
 *
 * « Voir plus · Tous mes lives » et « Ajouter un contrôle » sont la dernière LIGNE de leur carte.
 * Un gros bouton plein sous la carte pèserait plus lourd que le contenu qu'il prolonge — et il
 * ferait un deuxième cadre là où la carte-liste vient d'en économiser deux. Son encre est celle
 * des liens de l'univers (`text-link`) : marine chez le parent, l'orange d'écriture chez l'élève.
 */
export function CardListLinkRow(props: CardListLinkRowProps) {
    const { label, hint, iconName, onPress, testID } = props

    return (
        <Pressable testID={testID} onPress={onPress} disabled={onPress == null} accessibilityRole="button">
            <CardListRow>
                <Row gap="2" align="center">
                    {iconName != null && <Icon name={iconName} size={16} color="text-link" />}
                    <Row gap="1" align="center" style={styles.link}>
                        <Text size="text-md" weight="bold" color="text-link">
                            {label}
                        </Text>
                        {hint != null && hint !== "" && (
                            <Text size="text-md" color="text-muted">
                                {hint}
                            </Text>
                        )}
                    </Row>
                </Row>
            </CardListRow>
        </Pressable>
    )
}

export type CardListDropSlotProps = {
    /** La ligne qu'on déplace, telle qu'elle se rend dans la carte. */
    children: React.ReactNode
    testID?: string
}

/**
 * L'emplacement d'arrivée d'une ligne qu'on déplace, et la ligne soulevée posée dessus.
 *
 * Un ÉTAT d'affichage, pas un moteur : la carte-liste ne sait pas glisser. Le cadre en pointillé
 * dit où la ligne tombera si on la lâche ; la ligne, elle, se décolle de la carte (une surface,
 * une ombre) et se décale d'un rien, pour qu'on lise les deux. Tout tient DANS l'emplacement :
 * la carte rogne ce qui dépasse, la première et la dernière place comprises.
 */
export function CardListDropSlot(props: CardListDropSlotProps) {
    const { children, testID } = props

    return (
        <View testID={testID} style={styles.dropSlot}>
            <View style={styles.dropTarget} pointerEvents="none" />
            <View style={styles.lifted}>{children}</View>
        </View>
    )
}

/** De combien la ligne soulevée s'écarte de son emplacement : assez pour voir le pointillé dessous. */
const LIFT_OFFSET = 3

const styles = StyleSheet.create((theme) => ({
    /** `overflow` pour que la première et la dernière ligne épousent le rayon de la carte. */
    card: { overflow: "hidden" },
    dropSlot: {
        padding: theme.spacing["2"],
    },
    dropTarget: {
        position: "absolute",
        top: theme.spacing["1"],
        right: theme.spacing["1"],
        bottom: theme.spacing["1"],
        left: theme.spacing["1"],
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: theme.colors.role,
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["primary-soft"],
    },
    lifted: {
        backgroundColor: theme.colors["surface-raised"],
        borderRadius: theme.radius.md,
        transform: [{ translateX: LIFT_OFFSET }, { translateY: -LIFT_OFFSET }],
        ...shadowStyle(theme, "floating"),
    },
    link: { flexShrink: 1, flexWrap: "wrap" },
    divided: {
        borderTopWidth: 1,
        borderTopColor: theme.colors["border-card"],
    },
    row: (px: SpacingKey, py: SpacingKey) => ({
        paddingHorizontal: theme.spacing[px],
        paddingVertical: theme.spacing[py],
    }),
}))
